import { useState } from 'react';
import { Box, Stack, Divider } from '@mui/material';
import { Button, Typography, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { Close, ChevronLeft, ChevronRight, Copy, Check } from '@sweep-io/sweep-design/dist/icons';
import { BaseDialog } from '../../../common/dialogs/BaseDialog';
import { ArtifactCardIllustration } from '../ArtifactCardIllustration';
import { ArtifactTemplate } from './artifactTemplatesData';
import { telemetry } from '../../../../telemetry';

interface ArtifactTemplatePreviewModalProps {
  open: boolean;
  template: ArtifactTemplate | null;
  onClose: () => void;
  onUseTemplate: (template: ArtifactTemplate) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  currentIndex: number;
  totalCount: number;
  buttonLabel?: string;
}

export const ArtifactTemplatePreviewModal = ({
  open,
  template,
  onClose,
  onUseTemplate,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  currentIndex,
  totalCount,
  buttonLabel = 'Use Template',
}: ArtifactTemplatePreviewModalProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (!template) return;
    try {
      await navigator.clipboard.writeText(template.prompt);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      telemetry.captureError(error);
    }
  };

  if (!template) return null;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '720px',
          height: '580px',
          overflow: 'hidden',
        },
      }}
    >
      <Stack sx={{ height: '100%', position: 'relative' }}>
        <Box sx={{ position: 'absolute', right: '16px', top: '16px', zIndex: 2 }}>
          <IconButton variant="flat" size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <Stack direction="row" gap={2} py={3} alignItems="center" sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                width: '104px',
                height: '104px',
                borderRadius: '8px',
                backgroundColor: colors.blue[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {template.image ? (
                <img
                  src={template.image}
                  alt={template.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <ArtifactCardIllustration />
              )}
            </Box>
            <Stack gap={1} sx={{ minWidth: 0, flex: 1, pr: 4 }}>
              <Typography variant="h3" color={colors.grey[900]}>
                {template.title}
              </Typography>
              <Typography variant="body" color={colors.grey[700]}>
                {template.subtitle}
              </Typography>
              <Box>
                <Button size="small" onClick={() => onUseTemplate(template)}>
                  {buttonLabel}
                </Button>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ flexShrink: 0 }} />

          <Box sx={{ flex: 1, overflow: 'auto', pt: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body" color={colors.grey[700]}>
                Prompt
              </Typography>
              <Tooltip title={isCopied ? 'Copied!' : 'Copy prompt'} placement="top">
                <span>
                  <IconButton variant="flat" size="tiny" onClick={handleCopy}>
                    {isCopied ? <Check color={colors.grey[800]} /> : <Copy />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Box sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              <Typography variant="body" color={colors.grey[900]}>
                {template.prompt}
              </Typography>
            </Box>
          </Box>

          {/* Bottom Navigation */}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap={2}
            py={2}
            sx={{ flexShrink: 0 }}
          >
            <IconButton variant="flat" size="small" onClick={onPrevious} disabled={!hasPrevious}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="body" color={colors.grey[900]}>
              {currentIndex + 1} of {totalCount} Templates
            </Typography>
            <IconButton variant="flat" size="small" onClick={onNext} disabled={!hasNext}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Box>
      </Stack>
    </BaseDialog>
  );
};
